import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Row, Col, Card, Form, Button, Alert, Spinner, Badge, InputGroup, Modal } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import CreatableSelect from 'react-select/creatable'
import { amazonAPI } from '../api/amazonAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { formatDate, formatDateTime } from '../utils/commonUtils'
import { AmazonSettingsSectionHeader } from './AmazonScopeChip'
import { TAKES_EFFECT_NOTE } from './AmazonProductExceptionModal'
import { AmazonAnomalyRuleSummary, thresholdToDisplay } from './AmazonAnomalyRuleSummary'
import AmazonAnomalyDetectionGuideModal from './AmazonAnomalyDetectionGuideModal'
import '../styles/selectControl.css'

const CADENCE_LABELS = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' }
const UNIT_SUFFIX = { '%': '%', clicks: 'clicks', days: 'days', count: 'products', impressions: 'impressions', sessions: 'sessions' }
const DATA_COLLECTION_PATH = '/amazon-data-collection-settings'

// Percentages are stored as fractions (0.15) and edited as percents (15)
const toDisplay = thresholdToDisplay
const fromDisplay = (unit, value) => (unit === '%' ? Number(value) / 100 : Number(value))
const termsToOptions = (terms) => (terms || []).map(t => ({ value: t, label: t }))

const buildForm = (data) => ({
    daily_cap: String(data.daily_cap),
    min_weekly_impact: String(data.min_weekly_impact),
    brand_terms: termsToOptions(data.brand_terms),
    competitor_terms: termsToOptions(data.competitor_terms),
    rules: Object.fromEntries(data.rules.map(rule => [rule.key, {
        enabled: rule.enabled,
        thresholds: Object.fromEntries(rule.thresholds.map(t => [t.key, String(toDisplay(t.unit, t.value))])),
    }])),
})

/**
 * Anomaly Detection tab of Ad Automation Settings (per account). Reports unsaved edits through
 * ``onDirtyChange`` so the page can guard tab and account switches.
 */
function AmazonAnomalySettingsPanel({ accountId, canManage, onDirtyChange }) {
    const [settings, setSettings] = useState(null)
    const [form, setForm] = useState(null)
    const [savedForm, setSavedForm] = useState(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [loadError, setLoadError] = useState('')
    const [saved, setSaved] = useState(false)
    const [confirmReset, setConfirmReset] = useState(false)
    const [showGuide, setShowGuide] = useState(false)
    const inputRefs = useRef({})

    const dirty = !!form && !!savedForm && JSON.stringify(form) !== JSON.stringify(savedForm)
    useEffect(() => { onDirtyChange?.(dirty) }, [dirty, onDirtyChange])

    const applyData = (data) => {
        const built = buildForm(data)
        setSettings(data)
        setForm(built)
        setSavedForm(built)
    }

    const loadSettings = useCallback(async () => {
        if (!accountId) return
        setLoading(true)
        setLoadError('')
        setError('')
        setSaved(false)
        try {
            applyData(await amazonAPI.anomalySettings.get(accountId))
        } catch (err) {
            setLoadError(formatErrorResponse(err))
        } finally {
            setLoading(false)
        }
    }, [accountId])

    useEffect(() => { loadSettings() }, [loadSettings])

    const setField = (key, value) => { setForm(f => ({ ...f, [key]: value })); setSaved(false) }
    const setRule = (ruleKey, patch) => {
        setForm(f => ({ ...f, rules: { ...f.rules, [ruleKey]: { ...f.rules[ruleKey], ...patch } } }))
        setSaved(false)
    }
    const setThreshold = (ruleKey, key, value) => {
        setForm(f => ({
            ...f,
            rules: { ...f.rules, [ruleKey]: { ...f.rules[ruleKey], thresholds: { ...f.rules[ruleKey].thresholds, [key]: value } } },
        }))
        setSaved(false)
    }

    const resetAllThresholds = () => {
        setForm(f => ({
            ...f,
            rules: Object.fromEntries(settings.rules.map(rule => [rule.key, {
                ...f.rules[rule.key],
                thresholds: Object.fromEntries(rule.thresholds.map(t => [t.key, String(toDisplay(t.unit, t.default))])),
            }])),
        }))
        setSaved(false)
        setConfirmReset(false)
    }

    const focusField = (field) => {
        const el = inputRefs.current[field]
        if (el && typeof el.focus === 'function') el.focus()
    }

    const validate = () => {
        if (form.daily_cap === '' || !Number.isInteger(Number(form.daily_cap)) || Number(form.daily_cap) < 1) {
            return { message: 'Daily cap: enter a whole number of at least 1', field: 'daily_cap' }
        }
        if (form.min_weekly_impact === '' || Number.isNaN(Number(form.min_weekly_impact)) || Number(form.min_weekly_impact) < 0) {
            return { message: 'Minimum money at stake: enter an amount of 0 or more', field: 'min_weekly_impact' }
        }
        for (const rule of settings.rules) {
            for (const t of rule.thresholds) {
                const value = form.rules[rule.key].thresholds[t.key]
                if (value === '' || Number.isNaN(Number(value))) {
                    return { message: `${rule.label} — ${t.label}: enter a number`, field: `${rule.key}.${t.key}` }
                }
                if (t.unit === '%' && (Number(value) <= 0 || Number(value) > 100)) {
                    return { message: `${rule.label} — ${t.label}: enter a percentage above 0 and up to 100`, field: `${rule.key}.${t.key}` }
                }
            }
        }
        return null
    }

    const handleSave = async (e) => {
        e.preventDefault()
        setError('')
        setSaved(false)
        const problem = validate()
        if (problem) {
            setError(problem.message)
            focusField(problem.field)
            return
        }
        const thresholds = {}
        for (const rule of settings.rules) {
            thresholds[rule.key] = Object.fromEntries(rule.thresholds.map(t => [
                t.key, fromDisplay(t.unit, form.rules[rule.key].thresholds[t.key]),
            ]))
        }
        const payload = {
            daily_cap: Number(form.daily_cap),
            min_weekly_impact: Number(form.min_weekly_impact),
            brand_terms: form.brand_terms.map(o => o.value),
            competitor_terms: form.competitor_terms.map(o => o.value),
            disabled_rules: settings.rules.filter(r => !form.rules[r.key].enabled).map(r => r.key),
            thresholds,
        }
        setSaving(true)
        try {
            applyData(await amazonAPI.anomalySettings.update(accountId, payload))
            setSaved(true)
        } catch (err) {
            setError(err.response?.data?.error || formatErrorResponse(err))
            if (err.response?.data?.field) focusField(err.response.data.field)
        } finally {
            setSaving(false)
        }
    }

    if (loading) return <div className="text-center py-5"><Spinner animation="border" /></div>
    if (loadError) return <Alert variant="danger">{loadError}</Alert>
    if (!settings || !form) return null

    const families = settings.rules.reduce((acc, rule) => {
        const group = acc.find(g => g.key === rule.family)
        if (group) group.rules.push(rule)
        else acc.push({ key: rule.family, label: rule.family_label, rules: [rule] })
        return acc
    }, [])

    // The rule's sentence with the values currently in the form, so it updates while editing
    const renderSummary = (rule) => (
        <AmazonAnomalyRuleSummary rule={rule} displayValues={form.rules[rule.key].thresholds} />
    )

    const renderThreshold = (rule, t) => {
        const value = form.rules[rule.key].thresholds[t.key]
        const defaultDisplay = String(toDisplay(t.unit, t.default))
        const isDefault = value !== '' && Number(value) === Number(defaultDisplay)
        const fieldKey = `${rule.key}.${t.key}`
        return (
            <Form.Group as={Row} className="mb-2 align-items-center" key={t.key} controlId={fieldKey}>
                <Form.Label column sm={6} className="small">
                    {t.label}
                    {t.help && <div className="text-body-secondary small">{t.help}</div>}
                </Form.Label>
                <Col sm={6}>
                    <InputGroup size="sm">
                        {t.unit === '$' && <InputGroup.Text>$</InputGroup.Text>}
                        <Form.Control
                            type="number" step="any" min="0"
                            value={value}
                            disabled={!canManage}
                            ref={el => { inputRefs.current[fieldKey] = el }}
                            onChange={e => setThreshold(rule.key, t.key, e.target.value)}
                        />
                        {UNIT_SUFFIX[t.unit] && <InputGroup.Text>{UNIT_SUFFIX[t.unit]}</InputGroup.Text>}
                    </InputGroup>
                    <div className="small text-body-secondary">
                        Default {t.unit === '$' ? '$' : ''}{defaultDisplay}{t.unit === '%' ? '%' : ''}
                        {canManage && !isDefault && (
                            <Button variant="link" size="sm" className="p-0 ms-2 align-baseline"
                                onClick={() => setThreshold(rule.key, t.key, defaultDisplay)}>
                                Reset to default
                            </Button>
                        )}
                    </div>
                </Col>
            </Form.Group>
        )
    }

    const renderRule = (rule) => {
        const state = form.rules[rule.key]
        return (
            <div key={rule.key} className="border-top pt-3 mt-3">
                <div className="d-flex justify-content-between align-items-start gap-2">
                    <Form.Check
                        type="switch"
                        id={`rule-${rule.key}`}
                        label={<strong>{rule.label}</strong>}
                        checked={state.enabled}
                        disabled={!canManage}
                        onChange={e => setRule(rule.key, { enabled: e.target.checked })}
                    />
                    <div className="text-end small text-nowrap">
                        <Badge bg="secondary">{CADENCE_LABELS[rule.cadence] || rule.cadence}</Badge>
                        {rule.last_run && <div className="text-body-secondary">Last run {formatDate(rule.last_run)}</div>}
                    </div>
                </div>
                {state.enabled ? renderSummary(rule) : (
                    <div className="small text-warning-emphasis mb-2">
                        Off: on the next run, the open and dismissed alerts of this rule are auto-closed; alerts marked not useful are kept.
                    </div>
                )}
                {state.enabled && rule.thresholds.map(t => renderThreshold(rule, t))}
            </div>
        )
    }

    const breakEven = settings.break_even_inputs || {}

    return (
        <Form onSubmit={handleSave} style={{ maxWidth: '730px' }}>
            <div className="mb-3">
                <Button variant="link" className="p-0" onClick={() => setShowGuide(true)}>
                    <i className="fas fa-circle-question me-1"></i>How Anomaly Detection works
                </Button>
            </div>
            <AmazonAnomalyDetectionGuideModal show={showGuide} onHide={() => setShowGuide(false)} accountId={accountId}
                linkToSettings={false} />
            <Card className="mb-4">
                <Card.Body>
                    <AmazonSettingsSectionHeader
                        title="Daily list"
                        scope="anomaly"
                        scopeLabel="Anomaly Detection · this account"
                        sentence="How many Anomaly Detection alerts reach the daily list for this account. Inventory-Aware Bidding alerts are always shown."
                    />
                    <Form.Group as={Row} className="mb-3" controlId="daily_cap">
                        <Form.Label column sm={6}>
                            Daily cap
                            <div className="small text-body-secondary">
                                Most critical and warning alerts per run, most dollars first; the rest are shown as info. Default {settings.defaults.daily_cap}.
                            </div>
                        </Form.Label>
                        <Col sm={6}>
                            <Form.Control
                                type="number" min="1" step="1"
                                value={form.daily_cap}
                                disabled={!canManage}
                                ref={el => { inputRefs.current.daily_cap = el }}
                                onChange={e => setField('daily_cap', e.target.value)}
                            />
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} controlId="min_weekly_impact">
                        <Form.Label column sm={6}>
                            Minimum money at stake per week
                            <div className="small text-body-secondary">
                                Problems below this are shown as info (weekly summary). Default ${settings.defaults.min_weekly_impact}.
                            </div>
                        </Form.Label>
                        <Col sm={6}>
                            <InputGroup>
                                <InputGroup.Text>$</InputGroup.Text>
                                <Form.Control
                                    type="number" min="0" step="any"
                                    value={form.min_weekly_impact}
                                    disabled={!canManage}
                                    ref={el => { inputRefs.current.min_weekly_impact = el }}
                                    onChange={e => setField('min_weekly_impact', e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                    </Form.Group>
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Body>
                    <AmazonSettingsSectionHeader
                        title="Rules"
                        scope="anomaly"
                        scopeLabel="Anomaly Detection · this account"
                        sentence="Each rule can be switched off, and its thresholds tuned, for this account. The sentence under each rule shows what it does with the values below."
                        actions={canManage && (
                            <Button variant="outline-primary" size="sm" onClick={() => setConfirmReset(true)}>
                                Reset all to defaults
                            </Button>
                        )}
                    />
                    {families.map(family => (
                        <div key={family.key} className="mb-2">
                            <div className="text-uppercase small fw-semibold text-body-secondary mt-3">{family.label}</div>
                            {family.rules.map(renderRule)}
                        </div>
                    ))}
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Body>
                    <AmazonSettingsSectionHeader
                        title="Search terms"
                        scope="anomaly"
                        scopeLabel="Anomaly Detection · this account · not used yet"
                        sentence="Not used yet: these lists are for the search-term rules planned after the trial. They can be prepared now and take effect when those rules are added."
                    />
                    <Form.Group className="mb-3">
                        <Form.Label>Brand terms</Form.Label>
                        <CreatableSelect
                            isMulti
                            value={form.brand_terms}
                            onChange={opts => setField('brand_terms', opts || [])}
                            isDisabled={!canManage}
                            ref={el => { inputRefs.current.brand_terms = el }}
                            placeholder="Type a brand term and press Enter..."
                            noOptionsMessage={() => 'Type a term and press Enter'}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                        <Form.Text>Our own brand names, for judging spend on brand searches.</Form.Text>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Competitor terms</Form.Label>
                        <CreatableSelect
                            isMulti
                            value={form.competitor_terms}
                            onChange={opts => setField('competitor_terms', opts || [])}
                            isDisabled={!canManage}
                            ref={el => { inputRefs.current.competitor_terms = el }}
                            placeholder="Type a competitor brand and press Enter..."
                            noOptionsMessage={() => 'Type a term and press Enter'}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                        <Form.Text>Competitor brand names, for flagging competitor searches that do not convert.</Form.Text>
                    </Form.Group>
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Body>
                    <AmazonSettingsSectionHeader
                        title="Break-even inputs"
                        scope="global"
                        scopeLabel="All automations · all accounts"
                        sentence="Break-even ACoS uses these global values. They are shown here for reference and set in Data Collection Settings."
                    />
                    <Row className="small">
                        <Col sm={6} className="mb-2">
                            <div className="text-body-secondary">Sale price and fee window</div>
                            <div>{breakEven.units_sold_rolling_days ?? ''} days</div>
                        </Col>
                        <Col sm={6} className="mb-2">
                            <div className="text-body-secondary">Referral fee rate</div>
                            <div>{breakEven.referral_fee_rate !== null && breakEven.referral_fee_rate !== undefined ? `${+(breakEven.referral_fee_rate * 100).toFixed(2)}% of sale price` : ''}</div>
                        </Col>
                    </Row>
                    <Link to={DATA_COLLECTION_PATH}>Open Data Collection Settings</Link>
                </Card.Body>
            </Card>

            {canManage && (
                <div className="mb-4">
                    {error && <Alert variant="danger" className="mb-2">{error}</Alert>}
                    {saved && <Alert variant="success" className="mb-2">Settings saved.</Alert>}
                    <div className="small text-body-secondary mb-2">{TAKES_EFFECT_NOTE}</div>
                    <div className="d-flex align-items-center gap-3">
                        <Button type="submit" variant="primary" disabled={saving || !dirty}>
                            {saving ? <Spinner size="sm" /> : 'Save'}
                        </Button>
                        {settings.updated_at && (
                            <span className="small text-body-secondary">
                                Last saved {formatDateTime(settings.updated_at)}
                                {settings.updated_by_username ? ` by ${settings.updated_by_username}` : ''}
                            </span>
                        )}
                    </div>
                </div>
            )}

            <Modal show={confirmReset} onHide={() => setConfirmReset(false)} backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Reset all thresholds?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Every rule threshold for this account goes back to its default. Rule on/off switches, the daily list and the
                    search terms stay as they are. Nothing is saved until you click Save.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={() => setConfirmReset(false)}>Cancel</Button>
                    <Button variant="primary" onClick={resetAllThresholds}>Reset thresholds</Button>
                </Modal.Footer>
            </Modal>
        </Form>
    )
}

export default AmazonAnomalySettingsPanel
